import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import * as Dialog from "@radix-ui/react-dialog";

import styles from "./CompanyOrgStructure.module.css";
import { clampName, MAX_DESCRIPTION_LENGTH, MAX_NAME_LENGTH, MAX_TITLE_LENGTH } from "./orgStructureSchema";
import { cloneDocument, countSubtreeNodes, newId, sortRolesForDisplay, updateNode } from "./orgStructureMutations";
import { CompanyOrgStructureDocument, OrgPerson } from "./orgStructureTypes";

interface PersonDraft {
    id: string;
    name: string;
    role_id: string;
    isNew?: boolean;
}

interface OrgStructureNodeDialogProps {
    open: boolean;
    nodeId: string | null;
    document: CompanyOrgStructureDocument;
    onOpenChange: (open: boolean) => void;
    onSave: (document: CompanyOrgStructureDocument) => void;
    onDelete: () => void;
    onPersonClick?: (personId: string) => void;
}

function findNode(document: CompanyOrgStructureDocument, nodeId: string) {
    return document.nodes.find(node => node.id === nodeId);
}

export function OrgStructureNodeDialog({
    open,
    nodeId,
    document,
    onOpenChange,
    onSave,
    onDelete,
    onPersonClick
}: OrgStructureNodeDialogProps) {
    const { t, i18n } = useTranslation();
    const [titleDraft, setTitleDraft] = useState("");
    const [descriptionDraft, setDescriptionDraft] = useState("");
    const [peopleDraft, setPeopleDraft] = useState<PersonDraft[]>([]);
    const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);

    useEffect(() => {
        if (!open || !nodeId) {
            return;
        }
        const node = findNode(document, nodeId);
        if (!node) {
            return;
        }
        setTitleDraft(node.title);
        setDescriptionDraft(node.description);
        setPeopleDraft(
            node.people.map(person => ({
                id: person.id,
                name: person.name,
                role_id: person.role_id
            }))
        );
        setConfirmDeleteOpen(false);
    }, [open, nodeId, document]);

    const subtreeCount = nodeId ? countSubtreeNodes(document, nodeId) : 0;
    const sortedRoles = useMemo(
        () => sortRolesForDisplay(document.roles, i18n.language),
        [document.roles, i18n.language]
    );
    const defaultRoleId = sortedRoles[0]?.id ?? "";

    const handleAddPersonRow = () => {
        if (!defaultRoleId) {
            return;
        }
        setPeopleDraft(current => [
            ...current,
            { id: newId("person"), name: "", role_id: defaultRoleId, isNew: true }
        ]);
    };

    const handleSave = () => {
        if (!nodeId) {
            return;
        }
        let next = updateNode(document, nodeId, {
            title: titleDraft,
            description: descriptionDraft
        });
        next = cloneDocument(next);
        const node = findNode(next, nodeId);
        if (!node) {
            return;
        }

        node.people = peopleDraft
            .filter(person => person.name.trim() && person.role_id)
            .map(person => ({
                id: person.isNew ? newId("person") : person.id,
                name: clampName(person.name),
                role_id: person.role_id
            }));

        onSave(next);
        onOpenChange(false);
    };

    const handleDeleteClick = () => {
        if (subtreeCount > 1) {
            setConfirmDeleteOpen(true);
            return;
        }
        onDelete();
        onOpenChange(false);
    };

    const handlePersonClick = (person: OrgPerson | PersonDraft) => {
        onPersonClick?.(person.id);
    };

    return (
        <>
            <Dialog.Root open={open} onOpenChange={onOpenChange}>
                <Dialog.Portal>
                    <Dialog.Overlay className={styles.dialogOverlay} />
                    <Dialog.Content className={styles.dialogContent}>
                        <Dialog.Title className={styles.dialogTitle}>
                            {t("settingsPage.company.orgStructure.dialog.title")}
                        </Dialog.Title>
                        <Dialog.Description className={styles.dialogDescription}>
                            {t("settingsPage.company.orgStructure.dialog.description")}
                        </Dialog.Description>

                        <label className={styles.dialogFieldLabel}>
                            {t("settingsPage.company.orgStructure.dialog.nameLabel")}
                            <input
                                className={styles.dialogInput}
                                type="text"
                                value={titleDraft}
                                maxLength={MAX_TITLE_LENGTH}
                                placeholder={t("settingsPage.company.orgStructure.dialog.namePlaceholder")}
                                onChange={event => setTitleDraft(event.target.value)}
                            />
                        </label>

                        <label className={styles.dialogFieldLabel}>
                            {t("settingsPage.company.orgStructure.dialog.descriptionLabel")}
                            <textarea
                                className={styles.dialogTextarea}
                                value={descriptionDraft}
                                maxLength={MAX_DESCRIPTION_LENGTH}
                                placeholder={t("settingsPage.company.orgStructure.dialog.descriptionPlaceholder")}
                                onChange={event => setDescriptionDraft(event.target.value)}
                            />
                        </label>

                        <div className={styles.peopleSection}>
                            <h4 className={styles.peopleSectionTitle}>
                                {t("settingsPage.company.orgStructure.dialog.peopleTitle")}
                            </h4>
                            {document.roles.length === 0 ? (
                                <p className={styles.peopleHint}>
                                    {t("settingsPage.company.orgStructure.dialog.noRolesHint")}
                                </p>
                            ) : null}
                            <div className={styles.peopleList}>
                                {peopleDraft.map(person => (
                                    <div key={person.id} className={styles.personRow}>
                                        <input
                                            className={styles.personNameInput}
                                            type="text"
                                            value={person.name}
                                            maxLength={MAX_NAME_LENGTH}
                                            placeholder={t(
                                                "settingsPage.company.orgStructure.dialog.personNamePlaceholder"
                                            )}
                                            onChange={event =>
                                                setPeopleDraft(current =>
                                                    current.map(item =>
                                                        item.id === person.id
                                                            ? { ...item, name: event.target.value }
                                                            : item
                                                    )
                                                )
                                            }
                                            onClick={() => handlePersonClick(person)}
                                        />
                                        <select
                                            className={styles.personRoleSelect}
                                            value={person.role_id}
                                            disabled={document.roles.length === 0}
                                            onChange={event =>
                                                setPeopleDraft(current =>
                                                    current.map(item =>
                                                        item.id === person.id
                                                            ? { ...item, role_id: event.target.value }
                                                            : item
                                                    )
                                                )
                                            }
                                        >
                                            {sortedRoles.map(role => (
                                                <option key={role.id} value={role.id}>
                                                    {role.label}
                                                </option>
                                            ))}
                                        </select>
                                        <button
                                            type="button"
                                            className={styles.personRemoveButton}
                                            onClick={() =>
                                                setPeopleDraft(current =>
                                                    current.filter(item => item.id !== person.id)
                                                )
                                            }
                                        >
                                            {t("settingsPage.company.orgStructure.dialog.removePerson")}
                                        </button>
                                    </div>
                                ))}
                            </div>
                            <button
                                type="button"
                                className={styles.addPersonButton}
                                disabled={document.roles.length === 0}
                                onClick={handleAddPersonRow}
                            >
                                {t("settingsPage.company.orgStructure.dialog.addPerson")}
                            </button>
                        </div>

                        <div className={styles.dialogActions}>
                            <button type="button" className={styles.dialogDeleteButton} onClick={handleDeleteClick}>
                                {t("settingsPage.company.orgStructure.dialog.delete")}
                            </button>
                            <div className={styles.dialogPrimaryActions}>
                                <Dialog.Close asChild>
                                    <button type="button" className={styles.dialogCancelButton}>
                                        {t("settingsPage.account.cancel")}
                                    </button>
                                </Dialog.Close>
                                <button type="button" className={styles.dialogSaveButton} onClick={handleSave}>
                                    {t("settingsPage.account.save")}
                                </button>
                            </div>
                        </div>
                    </Dialog.Content>
                </Dialog.Portal>
            </Dialog.Root>

            {confirmDeleteOpen ? (
                <div className={styles.confirmOverlay} role="dialog" aria-modal="true">
                    <div className={styles.confirmDialog}>
                        <h3 className={styles.confirmTitle}>
                            {t("settingsPage.company.orgStructure.deleteNodeConfirm.title")}
                        </h3>
                        <p className={styles.confirmMessage}>
                            {t("settingsPage.company.orgStructure.deleteNodeConfirm.message", {
                                count: subtreeCount - 1
                            })}
                        </p>
                        <div className={styles.confirmActions}>
                            <button
                                type="button"
                                className={styles.dialogCancelButton}
                                onClick={() => setConfirmDeleteOpen(false)}
                            >
                                {t("settingsPage.account.cancel")}
                            </button>
                            <button
                                type="button"
                                className={styles.dialogDeleteButton}
                                onClick={() => {
                                    onDelete();
                                    setConfirmDeleteOpen(false);
                                    onOpenChange(false);
                                }}
                            >
                                {t("settingsPage.company.orgStructure.dialog.delete")}
                            </button>
                        </div>
                    </div>
                </div>
            ) : null}
        </>
    );
}
